/**
 * Navigation helpers for the cockpit. They mirror the Redux choreography the
 * sidebar / search / deeplinks already use so the target screens open in the
 * same state as when reached from there.
 */
import { TOGGLE_DRAWER } from "../../helper/constants";
import { callAPI } from "../../helper/apiUtils";
import { $crud } from "../../helper/CrudFactory";
import { findRoomInLists, resolvePinnedRoom } from "../Project/roomDnd";
import { isTeamsChat, teamsChatTypeOf } from "../../utils/teams/teamsChats";
import {
  GROUPS,
  GROUP_CHATS,
  OPEN_CHATS,
  OPEN_PROJECTS,
  RIGHT_TOP,
  SET_ACTIVE_PINNED_ROOM_ID,
  SET_CHAT_FULLSCREEN,
  SET_DEEPLINK_DATA,
  SET_DEEPLINK_LOADING,
  SET_ACTIVITY_MESSAGES,
  SET_ACTIVITY_MESSAGES_LAST_FETCHED,
  SET_IN_ACTIVITY,
  SET_OPEN_SIDE_BAR,
  SET_PENDING_CHAT,
  SET_PENDING_EMAIL,
  SET_PENDING_PROJECT_TASK,
  TOGGLE_CHAT,
  TOGGLE_LATEST_MSG,
  TOGGLE_PROJECT,
  TOGGLE_TODO_VIEW,
  TOGGLE_WORK_AREA,
  UPDATE_PROJECT_ACTION_FIRE,
  DRAFT_MESSAGES,
  store,
} from "../../store";

function resetChatLayout(dispatch) {
  dispatch({ type: RIGHT_TOP, rightTop: "" });
  dispatch({ type: SET_DEEPLINK_DATA, payload: null });
  dispatch({ type: SET_DEEPLINK_LOADING, loading: false });
  dispatch({ type: OPEN_PROJECTS, openProjects: [] });
  dispatch({ type: OPEN_CHATS, openChats: [] });
  dispatch({ type: SET_CHAT_FULLSCREEN, payload: false });
  dispatch({ type: SET_ACTIVE_PINNED_ROOM_ID, payload: null });
}

/** Nachrichten page. `view: 'email'` lands on the inbox, otherwise the chat list. */
export function openMessagesPage(dispatch, history, { view } = {}) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: GROUPS, groups: [] });
  dispatch({ type: SET_IN_ACTIVITY, status: true });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "LatestMessages" });
  history.push({
    pathname: "/messages",
    search: view === "email" ? "?view=email" : "",
    state: view === "email" ? { openEmailTab: true } : undefined,
  });
}

/** News feed. */
export function openNewsPage(dispatch, history) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: GROUPS, groups: [] });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "articles" });
  history.push("/articles");
}

/** Alle Räume list (RightBar with MyProject). */
export function openRoomsList(dispatch, history) {
  dispatch({ type: SET_ACTIVE_PINNED_ROOM_ID, payload: null });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: TOGGLE_PROJECT, payload: false });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: SET_CHAT_FULLSCREEN, payload: false });
  dispatch({ type: OPEN_PROJECTS, openProjects: [] });
  dispatch({ type: OPEN_CHATS, openChats: [] });
  dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
  dispatch({ type: RIGHT_TOP, rightTop: "project1" });
  dispatch({ type: TOGGLE_DRAWER, open: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
  history.push("/projects");
}

/**
 * Open a single room. Uses the same path as a pinned room in the sidebar.
 * `room` needs at least { id, name }; extra fields from Redux lists win.
 */
export function openRoom(dispatch, history, room, reduxProjects, internalProjectsAll) {
  if (!room || !room.id) return openRoomsList(dispatch, history);
  const pin = {
    id: room.id,
    name: room.name,
    image: room.image || null,
    themeColor: room.themeColor || null,
    isInternal: !!room.isInternal,
    isJoin: true,
    isDone: false,
  };
  const live = findRoomInLists(room.id, reduxProjects, internalProjectsAll);
  const resolved = live ? resolvePinnedRoom(pin, reduxProjects, internalProjectsAll) : { ...pin, ...room };
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: TOGGLE_PROJECT, payload: true });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: SET_CHAT_FULLSCREEN, payload: false });
  dispatch({ type: SET_ACTIVE_PINNED_ROOM_ID, payload: null });
  dispatch({ type: OPEN_CHATS, openChats: [] });
  dispatch({ type: OPEN_PROJECTS, openProjects: [resolved] });
  dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
  dispatch({ type: RIGHT_TOP, rightTop: "project1" });
  dispatch({ type: TOGGLE_DRAWER, open: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "project1" });
  history.push("/projects");
}

/**
 * Open a task: its project, on Aufgaben, with the task open there. Tasks only
 * live inside projects. With only the task id known, GET /todo/:id gives the
 * project; a task without one (old, hidden) opens nothing. Resolves true when
 * the project was opened.
 */
export async function openProjectTask(dispatch, history, { roomId, roomName, todoId, isInternal, companyId } = {}) {
  const hasTodo = todoId != null && todoId !== "";
  let id = roomId != null && roomId !== "" ? String(roomId) : null;
  let name = roomName || "";
  if (!id && hasTodo) {
    const room = await taskRoomOf(todoId);
    if (room) {
      id = room.id;
      if (!name) name = room.name;
    }
  }
  if (!id) return false;
  const { projects, internalProjectsAll } = store.getState();
  // Picked up by the project (switches to Aufgaben) and its task pane (opens the task).
  // `at`: a request nobody picked up within a minute is dropped (pendingProjectTaskIsFresh).
  dispatch({ type: SET_PENDING_PROJECT_TASK, payload: { roomId: id, todoId: hasTodo ? String(todoId) : null, at: Date.now() } });
  openRoom(dispatch, history, { id, name, isInternal: !!(isInternal || companyId) }, projects, internalProjectsAll);
  return true;
}

/** A task's project ({ id, name }) from GET /todo/:id; null when it has none (or the call fails). */
export async function taskRoomOf(todoId) {
  if (todoId == null || todoId === "") return null;
  try {
    const { data } = await callAPI(`/todo/${todoId}`, {}, "GET");
    const raw = data && data.data;
    // GET /todo/:id can answer with another task: only take this one.
    if (!raw || String(raw.todoId ?? raw.id) !== String(todoId)) return null;
    const rid = raw.roomId || raw.buildingList;
    return rid ? { id: String(rid), name: raw.roomName || "" } : null;
  } catch (_) {
    return null;
  }
}

/** True for a pending "open this project's task" request of the last minute. */
export function pendingProjectTaskIsFresh(pending) {
  return !!pending && Date.now() - (Number(pending.at) || 0) < 60000;
}

/**
 * Open a chat inside Nachrichten and, when a message is given, scroll to and
 * highlight it. The cockpit never routes a chat through /projects or the
 * chat deeplink: the conversation always opens on the Messages screen, next
 * to the chat list, the same way clicking the row there would.
 */
export function openChatMessage(dispatch, history, chat) {
  const chatGroupId = chat && (chat.chatGroupId || chat.groupId || (chat.group && chat.group.id));
  if (!chatGroupId) return openMessagesPage(dispatch, history);
  const roomId = chat.roomId || chat.buildingList || null;
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_DRAWER, open: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
  dispatch({ type: TOGGLE_PROJECT, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: true });
  dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
  dispatch({ type: TOGGLE_CHAT, payload: false });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "LatestMessages" });
  if (history) history.push({ pathname: "/messages", search: "", state: { fromCockpit: true } });
  // Last, so the Messages screen has already settled into its activity layout
  // when it picks the request up — the layout pass clears open chats.
  dispatch({
    type: SET_PENDING_CHAT,
    payload: {
      chatGroupId: String(chatGroupId),
      messageId: chat.messageId ? String(chat.messageId) : null,
      page_no: Number(chat.page_no) > 0 ? Number(chat.page_no) : 1,
      roomId: roomId ? String(roomId) : null,
      companyId: chat.companyId ? String(chat.companyId) : null,
      isInternal: !!(chat.isInternal || chat.companyId),
      isOneToOne: chat.isOneToOne == null ? null : !!chat.isOneToOne,
      name: chat.name || "",
      // A Microsoft Teams chat (no room): Messages opens it in the room-less pane in its Teams mode.
      ...(isTeamsChat(chat) ? { isTeams: true, source: "teams", teamsChatType: teamsChatTypeOf(chat), webUrl: chat.webUrl || null } : {}),
    },
  });
}

/**
 * Where a message sits in its chat: the transcript page the conversation has
 * to load for it, plus the group's shape so the Messages screen can open the
 * right pane for a chat that is not in the activity list.
 *
 * The page is resolved on every click, never stored: messages sent since the
 * source was saved push older ones onto later pages. Without the right page
 * the conversation loads a slice the message is not on and the highlight
 * never fires. `page` is 0 when the lookup failed, so the caller can fall
 * back to whatever page it already knew.
 */
export async function locateChatMessage(groupId, messageId) {
  const fallback = { page: 0, roomId: null, isOneToOne: null, name: "", companyId: null };
  if (!groupId) return fallback;
  try {
    const { data } = await callAPI(
      `/group/${encodeURIComponent(groupId)}/messagePage`,
      { messageId: messageId || "", limit: 30 },
      "GET"
    );
    if (!data || !data.status) return fallback;
    return {
      page: Number(data.page_no) > 0 ? Number(data.page_no) : 1,
      roomId: data.buildingList ? String(data.buildingList) : null,
      isOneToOne: !!data.isOneToOne,
      name: data.name || "",
      companyId: data.companyId ? String(data.companyId) : null,
      // A Microsoft Teams mirror (messagePage adds source / isTeams / teamsChatType / webUrl).
      ...(isTeamsChat(data) ? { isTeams: true, source: "teams", teamsChatType: teamsChatTypeOf(data), webUrl: data.webUrl || null } : {}),
    };
  } catch (_) {
    return fallback;
  }
}

/** Open a chat row from GET /group/getUserChats. */
export function openChat(dispatch, history, chat) {
  const groupId = chat?.group?.id || chat?.groupId;
  if (!groupId) return openMessagesPage(dispatch, history);
  openChatMessage(dispatch, history, {
    chatGroupId: String(groupId),
    roomId: chat?.buildingList ? String(chat.buildingList) : null,
    companyId: chat?.companyId ? String(chat.companyId) : null,
    isInternal: !!chat?.isInternal,
    isOneToOne: chat?.group?.isOneToOne,
    // A Teams row's own name is its display name (the other person of a 1:1, else the topic).
    name: isTeamsChat(chat) ? chat?.name || chat?.group?.name || "" : chat?.group?.name || chat?.name || "",
    ...(isTeamsChat(chat) ? { isTeams: true, teamsChatType: teamsChatTypeOf(chat), webUrl: chat.webUrl || chat.group?.webUrl || null } : {}),
  });
}

/** Open an email (e.g. the latest daily report) inside Nachrichten. */
export function openEmail(dispatch, history, email) {
  if (!email || !email.id) return openMessagesPage(dispatch, history, { view: "email" });
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_DRAWER, open: false });
  dispatch({
    type: SET_PENDING_EMAIL,
    payload: { id: email.id, emailAccountId: email.emailAccountId || email.emailAccount || null },
  });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
  dispatch({ type: TOGGLE_PROJECT, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: true });
  dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
  dispatch({ type: TOGGLE_CHAT, payload: false });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "LatestMessages" });
  history.push({ pathname: "/messages", search: "", state: { fromCockpit: true } });
}

/** Personal settings, optionally scrolled to a section (ProfileSetting reads `openSection`). */
export function openSettings(dispatch, history, section) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "profile" });
  // fromCockpit: the sub-page's back button returns to the Cockpit instead of the settings list.
  history.push({ pathname: "/profile-setting", state: section ? { openSection: section, fromCockpit: true } : undefined });
}

export function openCockpit(dispatch, history) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "cockpit" });
  history.push("/");
}

export function openAddCompany(dispatch, history) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "my-companies" });
  history.push("/add-company");
}

export function openMyCompanies(dispatch, history) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "my-companies" });
  history.push("/my-companies");
}

export function openCompanyDocuments(dispatch, history, companyId) {
  if (!companyId) return openMyCompanies(dispatch, history);
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "company-documents" });
  try {
    sessionStorage.setItem("kjuup_intern_docs_company_id", String(companyId));
  } catch (_) {}
  history.push(`/company-documents/${companyId}`);
}

export function openKjuupIoAbo(dispatch, history, companyId) {
  if (!companyId) return;
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "cockpit" });
  history.push(`/kjuup-io/${companyId}`);
}

/** Company admin of a Kjuup company with Stripe: assigns the Kjuup.io seats. */
export function viewerAssignsCompanySeats(company) {
  if (!company || !company.isKjuup || company.stripeFeatureEnabled === false) return false;
  if (company.isSuperAdmin || company.myRole === "ROLE_COMPANY_SUPER_ADMIN") return true;
  return String(company.myPosition || "").trim().toLowerCase() === "company admin";
}

/**
 * Manage the active Kjuup.io subscription (sidebar › Kjuup.io). Kjuup+ company
 * admins land on the company's seats, everyone else on the personal plan page.
 * `company` (optional): the company the cockpit has selected.
 */
export function openKjuupIoSubscription(dispatch, history, { personaInfo, subscription, company } = {}) {
  if (personaInfo && personaInfo.isKjuupPlusMember) {
    if (viewerAssignsCompanySeats(company) && company.id != null) {
      openKjuupIoAbo(dispatch, history, company.id);
      return;
    }
    const billedId = subscription && subscription.companyBilling && subscription.companyBilling.companyId;
    if (personaInfo.isCompanyAdmin && billedId) {
      openKjuupIoAbo(dispatch, history, billedId);
      return;
    }
  }
  history.push("/subscription");
}

/**
 * Admin-Bereich of a company: its sections are part of the company settings.
 * `section`: an Admin-Panel section anchor (e.g. "dept-employees") opens that
 * section directly. `state` (optional): what the caller already knows about the
 * company — { mediaExportEnabled, employeeCount, documentCount } from /user/cockpit.
 */
export function openCompanyAdmin(dispatch, history, companyId, section, state) {
  if (!companyId) return openMyCompanies(dispatch, history);
  return openCompanySettingsFor(dispatch, history, { id: companyId }, { section, state });
}

export function openCompanyDetails(dispatch, history, companyId) {
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "cockpit" });
  history.push({
    pathname: "/cockpit",
    search: companyId ? `?tab=company&company=${encodeURIComponent(companyId)}` : "?tab=company",
  });
}

/**
 * Company settings (/company-setting/:id/:deptId). `opts.section`: an
 * Admin-Panel section to open there; `opts.state`: extra navigation state
 * (index counts, media export flag); `opts.replace`: replace the entry.
 */
export function openCompanySettings(dispatch, history, companyId, deptId, isKjuup, opts = {}) {
  if (!companyId) return openMyCompanies(dispatch, history);
  resetChatLayout(dispatch);
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: false });
  const dept = deptId && String(deptId) !== "undefined" ? deptId : "undefined";
  const target = {
    pathname: `/company-setting/${companyId}/${dept}`,
    hash: opts.section ? `#${opts.section}` : "",
    state: { ...(opts.state || {}), isKjuup: !!isKjuup, from: "cockpit" },
  };
  if (opts.replace) history.replace(target);
  else history.push(target);
}

/**
 * Company settings for a company entry ({ id, deptLocationId?, isKjuup? }), with
 * the department the settings work on: a department admin's own department,
 * else the company's first department (as the app's openCompanySettings).
 * Used by the Cockpit company page, My companies and the Admin-Bereich links.
 */
export async function openCompanySettingsFor(dispatch, history, company, opts = {}) {
  if (!company || company.id == null) return openMyCompanies(dispatch, history);
  let deptId = company.deptLocationId || null;
  let isKjuup = company.isKjuup;
  try {
    const { data } = await callAPI(`/company/${company.id}/profile`, {}, "GET");
    const profile = data?.profile || {};
    if (profile.isKjuup != null) isKjuup = profile.isKjuup;
    if (profile.role === "ROLE_COMPANY_DEPARTMENT_ADMIN" && profile.deptLocationId) {
      deptId = profile.deptLocationId;
    } else {
      const deptRes = await callAPI(`/company/getDeptLocation/${company.id}`, {}, "GET");
      const first = deptRes?.data?.data?.getCompanyDept?.[0]?.id;
      if (first) deptId = first;
    }
  } catch (_) {
    // open with what we have
  }
  return openCompanySettings(dispatch, history, company.id, deptId || "undefined", isKjuup, opts);
}

/** Open a URL in a new tab (external products, Kjuup+ request form). */
export function openExternal(url) {
  if (!url) return;
  try {
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (_) {
    window.location.href = url;
  }
}

/**
 * Prefill the Kjuup Pilot input. KjuupPilotChat reads its draft from Redux
 * `draftMessages` (and localStorage) keyed by group id.
 */
export function prefillPilotDraft(dispatch, existingDrafts, userId, text) {
  const drafts = Array.isArray(existingDrafts) ? existingDrafts : [];
  const next = drafts.filter((m) => m && m.group !== "kjuup-pilot");
  if (text) next.push({ userId, group: "kjuup-pilot", message: text });
  try {
    localStorage.setItem("draftMessage", JSON.stringify(next));
  } catch (_) {}
  dispatch({ type: DRAFT_MESSAGES, draftMessages: next });
}

/** Open or create the 1:1 chat with a company admin. */
export async function openDirectChat(dispatch, history, { userId, name, avatar, companyId, currentUserId, activityMessages }) {
  if (!dispatch || !userId || !currentUserId || String(userId) === String(currentUserId)) return false;
  const payload = {
    groupType: "user",
    isOneToOne: true,
    users: [currentUserId, userId],
  };
  if (companyId) payload.companyId = companyId;
  const { data } = await callAPI("/group", {}, "POST", payload);
  if (!data || !data.status || !data.chat) {
    $crud.notify({ type: "error", message: (data && data.message) || "Chat could not be opened." });
    return false;
  }
  const chatData = data.chat;
  const memberName = name || chatData.name || "";
  const chatUsers =
    Array.isArray(chatData.users) && chatData.users.some((u) => u && String(u.id) === String(userId))
      ? chatData.users
      : [
          { id: currentUserId },
          { id: userId, fullName: memberName, avatar: avatar || null },
        ];
  const activityMessageEntry = {
    id: chatData.id,
    mainId: chatData.id,
    group: {
      ...chatData,
      id: chatData.id,
      name: memberName,
      isOneToOne: true,
      isInternal: Boolean(chatData.isInternal),
      users: chatUsers,
    },
    senderId: userId,
    senderName: memberName,
    senderAvatar: avatar || null,
    name: memberName,
    avatar: avatar || null,
    isOpened: true,
    isOneToOne: true,
    unseenChatCount: 0,
    createdAt: new Date().toISOString(),
  };
  const chatPayload = {
    ...chatData,
    id: chatData.id,
    key: chatData.id,
    name: memberName,
    isOpened: true,
    isOneToOne: true,
    avatar: avatar || null,
    defaultChatGroup: Boolean(chatData.defaultChatGroup),
    isInternal: Boolean(chatData.isInternal),
    onlyView: false,
    unseenChatCount: 0,
  };
  dispatch({ type: OPEN_PROJECTS, openProjects: [] });
  dispatch({ type: GROUPS, groups: [] });
  dispatch({ type: GROUP_CHATS, groupChats: [] });
  dispatch({ type: TOGGLE_LATEST_MSG, payload: true });
  dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
  dispatch({ type: TOGGLE_PROJECT, payload: false });
  dispatch({ type: SET_IN_ACTIVITY, status: true });
  dispatch({ type: TOGGLE_WORK_AREA, toggleWorkArea: true });
  dispatch({ type: TOGGLE_CHAT, payload: false });
  dispatch({ type: SET_OPEN_SIDE_BAR, payload: "LatestMessages" });
  dispatch({ type: RIGHT_TOP, rightTop: "" });
  const currentMessages = Array.isArray(activityMessages) ? activityMessages : [];
  const updatedMessages = currentMessages
    .filter((msg) => !(msg && msg.group && msg.group.id === chatData.id))
    .map((msg) => ({ ...msg, isOpened: false }));
  dispatch({ type: SET_ACTIVITY_MESSAGES, messages: [activityMessageEntry, ...updatedMessages] });
  dispatch({ type: SET_ACTIVITY_MESSAGES_LAST_FETCHED, payload: Date.now() });
  dispatch({ type: OPEN_CHATS, openChats: [chatPayload] });
  if (history) history.push("/messages");
  return true;
}
